import { uebersetze } from '../i18n/sprache.js';

/**
 * Was der Server an einem Formular auszusetzen hat, in Klartext.
 *
 * Der haeufigste Fehler ueberhaupt - ein Tippfehler in einem Formular -
 * kam bisher als "Request validation failed" heraus. Englisch, und vor
 * allem: ohne jede Angabe, WELCHES Feld gemeint ist. Die Angaben waren da,
 * unter `error.details.fields`, aber nur fuenf von rund 140 Stellen im
 * Frontend haben sie ausgelesen. Ueberall sonst stand der nichtssagende
 * Satz.
 *
 * Uebersetzt wird hier und nicht im Dienst, weil nur der Browser weiss,
 * welche Sprache jemand eingestellt hat. Ein Dienst, der Deutsch
 * zurueckgibt, waere fuer einen franzoesischen Nutzer genauso unbrauchbar
 * wie das englische Joi-Original.
 *
 * Die Regelnamen (`string.email`, `any.required`) kommen von Joi und sind
 * ein fester, kleiner Satz. Was hier fehlt, faellt auf Joi's eigene Meldung
 * zurueck - unschoen, aber nie leer.
 */

/** Ein Pfad wie 'admin.email' oder 'lines.0.amount' als Feldname. */
export function feldName(pfad) {
  const teile = String(pfad || '').split('.');
  /* Zahlen sind Listenplaetze, keine Felder. 'lines.0.amount' soll
     "amount" heissen und nicht "0". */
  const letztes = [...teile].reverse().find((t) => t && !/^\d+$/.test(t));
  const schluessel = `feld.${letztes}`;
  const uebersetzt = uebersetze(schluessel);
  // uebersetze() gibt den Schluessel zurueck, wenn es ihn nicht kennt.
  return uebersetzt === schluessel ? letztes || pfad : uebersetzt;
}

/**
 * Hat das Schema hier eine EIGENE Meldung hinterlegt?
 *
 * Manche Felder tragen eine handgeschriebene Erklärung, und die ist der
 * allgemeinen Regel überlegen: `slug` sagt "Kleinbuchstabe am Anfang, danach
 * Kleinbuchstaben, Ziffern oder Unterstrich, 2 bis 24 Zeichen" statt bloss
 * "hat nicht die erwartete Form". Solche Meldungen zu ersetzen wäre eine
 * Verschlechterung, kein Feinschliff.
 *
 * Erkennbar sind sie daran, was Joi selbst schreibt: seine eigenen Meldungen
 * beginnen IMMER mit dem Feldpfad in Anführungszeichen ("admin.email" must be
 * a valid email). Fehlt dieser Anfang, hat jemand die Meldung von Hand
 * gesetzt.
 */
function eigeneMeldung(feld) {
  const text = String(feld?.message || '');
  if (!text) {
    return null;
  }
  const joiVorgabe = new RegExp(`^"${String(feld?.path || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`);
  return joiVorgabe.test(text) ? null : text;
}

/** Ein einzelner Befund als Satz. */
export function feldFehlerText(feld) {
  const name = feldName(feld?.path);
  const regel = String(feld?.type || '');
  const grenze = feld?.limit;

  /* Eine eigene Meldung des Schemas gewinnt - immer. Sie steht dort, weil
     jemand fand, dass die allgemeine Regel hier nicht reicht. */
  const eigen = eigeneMeldung(feld);
  if (eigen) {
    return eigen;
  }

  const schluessel = `feldfehler.${regel}`;
  const text = uebersetze(schluessel, { feld: name, grenze });
  if (text !== schluessel) {
    return text;
  }

  /* Unbekannte Regel: Joi's eigene Meldung, aber wenigstens mit dem
     uebersetzten Feldnamen davor - so weiss man, wo man suchen muss. */
  return feld?.message ? `${name}: ${feld.message}` : `${name}: ${uebersetze('feldfehler.unbekannt')}`;
}

/**
 * Alle Befunde zu einem Satz.
 *
 * Ab vier Feldern wird nur noch gezaehlt: eine Aufzaehlung von zwoelf
 * Feldnamen liest niemand, und die roten Markierungen am Formular sagen
 * dasselbe genauer.
 */
export function feldFehlerMeldung(fields) {
  const liste = Array.isArray(fields) ? fields.filter(Boolean) : [];
  if (liste.length === 0) {
    return null;
  }
  if (liste.length > 3) {
    return uebersetze('feldfehler.mehrere', { anzahl: liste.length });
  }
  return liste.map(feldFehlerText).join(' ');
}
